import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, User, Hotel, Car, MapPin, Package, DollarSign, FileText, CheckCircle, Clipboard } from "lucide-react";
import { ClientInfoStepSimple } from "@/components/package-steps/ClientInfoStepSimple";
import { TripDetailsAndProgramStep } from "@/components/package-steps/TripDetailsAndProgramStep";
import { HotelStep } from "@/components/package-steps/HotelStep";
import { TransportStep } from "@/components/package-steps/TransportStep";
import { ToursStep } from "@/components/package-steps/ToursStep";
import { ServicesStep } from "@/components/package-steps/ServicesStep";
import { PricingStep } from "@/components/package-steps/PricingStep";
import { SummaryStep } from "@/components/package-steps/SummaryStep";
import { InclusionsStep } from "@/components/package-steps/InclusionsStep";
import { generatePackagePDF } from "@/utils/pdfGenerator";
import { usePackageDraft } from "@/hooks/usePackageDraft";
import { AutoSaveIndicator } from "@/components/package-steps/AutoSaveIndicator";
import { PackageSidebarSummary } from "@/components/package-steps/PackageSidebarSummary";
import { validateClientInfo, validateTripDetails } from "@/utils/packageValidation";
import type { PackageItem } from "@/types/package-item";
import type { Service } from "@/types/service";

export interface PackageData {
  // Package Info
  packageName: string;
  destination: string;
  duration: string;
  notes: string;

  // Client Info
  clientType: 'individual' | 'b2b';
  clientName: string;
  clientEmail: string;
  clientPhone: string;
  clientAddress: string;
  companyName?: string;
  contactPerson?: string;
  companyId?: string;
  companyLogoUrl?: string;
  addClientToDB?: boolean;

  // Package Details
  numberOfRooms: number;
  numberOfNights: number;
  mealPlans: ('breakfast' | 'lunch' | 'dinner' | 'none')[];
  adultsCount: number;
  childrenCount: number;
  infantsCount: number;

  // Transport Details
  receptionCarsCount: number;
  tourCarsCount: number;
  receptionVehicleTypeId?: string;
  receptionVehicleTypeName?: string;
  tourVehicleTypeId?: string;
  tourVehicleTypeName?: string;

  // International Flights
  internationalBaggageCount: number;
  internationalBaggageWeight: number;
  internationalCabinWeight: number;

  // Domestic Flights
  domesticBaggageCount: number;
  domesticBaggageWeight: number;
  domesticCabinWeight: number;

  // Cities plan
  citiesPlan: Array<{
    countryId?: string;
    countryName?: string;
    cityId?: string;
    cityName?: string;
    hotel?: string;
    hotelId?: string;
    hotelName?: string;
    roomType?: string;
    roomId?: string;
    roomName?: string;
    transferVehicleTypeId?: string;
    transferVehicleTypeName?: string;
    tourVehicleTypeId?: string;
    tourVehicleTypeName?: string;
    meals?: { breakfast?: boolean; lunch?: boolean; dinner?: boolean };
    roomsCount?: number;
    nightsCount?: number;
  }>;

  // Package Inclusions
  includesHotels: boolean;
  includesInternationalFlights: boolean;
  includesDomesticFlights: boolean;
  includesTransport: boolean;
  includesTours: boolean;
  includesServices: boolean;

  // Hotels
  hotels: Array<{
    name: string;
    location: string;
    checkIn: string;
    checkOut: string;
    roomType: string;
    nights: number;
    pricePerNight: number;
  }>;

  // Flights
  flights: Array<{
    type: 'outbound' | 'return' | 'internal';
    from: string;
    to: string;
    fromCountry?: string;
    fromCity?: string;
    toCountry?: string;
    toCity?: string;
    date: string;
    time: string;
    airline: string;
    class: string;
    price: number;
    costPrice?: number;
    sellingPrice?: number;
    adults: number;
    children: number;
    infants: number;
    transitFlight?: boolean;
    transitDuration?: number;
    supplier?: string;
    notes?: string;
  }>;

  // Transport
  transport: Array<{
    type: 'airport_transfer' | 'city_transport' | 'tour_transport';
    vehicle: string;
    from: string;
    to: string;
    date: string;
    price: number;
  }>;

  // Tours
  tours: Array<{
    name: string;
    description: string;
    duration: string;
    date: string;
    price: number;
    included: string[];
  }>;

  // Inclusions
  inclusions?: PackageItem[];
  exclusions?: PackageItem[];

  // Services
  services: Service[];

  // Pricing
  basePrice: number;
  profitType?: 'percentage' | 'fixed';
  profitValue?: number;
  profitAmount?: number;
  discount: number;
  taxes: number;
  totalPrice: number;
  paymentTerms: string;
}

const initialPackageData: PackageData = {
  packageName: '',
  destination: '',
  duration: '',
  notes: '',
  clientType: 'individual',
  clientName: '',
  clientEmail: '',
  clientPhone: '',
  clientAddress: '',
  companyName: '',
  contactPerson: '',
  companyId: '',
  companyLogoUrl: '',
  numberOfRooms: 1,
  numberOfNights: 7,
  mealPlans: ['breakfast'],
  adultsCount: 2,
  childrenCount: 0,
  infantsCount: 0,
  receptionCarsCount: 1,
  tourCarsCount: 1,
  receptionVehicleTypeId: '',
  receptionVehicleTypeName: '',
  tourVehicleTypeId: '',
  tourVehicleTypeName: '',
  internationalBaggageCount: 1,
  internationalBaggageWeight: 23,
  internationalCabinWeight: 7,
  domesticBaggageCount: 1,
  domesticBaggageWeight: 20,
  domesticCabinWeight: 7,
  citiesPlan: [],
  includesHotels: true,
  includesInternationalFlights: true,
  includesDomesticFlights: false,
  includesTransport: true,
  includesTours: true,
  includesServices: false,
  hotels: [],
  flights: [],
  transport: [],
  tours: [],
  inclusions: [],
  exclusions: [],
  services: [],
  basePrice: 0,
  profitType: 'percentage',
  profitValue: 20,
  profitAmount: 0,
  discount: 0,
  taxes: 0,
  totalPrice: 0,
  paymentTerms: ''
};

const steps = [
  { id: 1, title: "معلومات العميل", icon: User, description: "بيانات العميل الأساسية" },
  { id: 2, title: "تفاصيل الرحلة والبرنامج", icon: Clipboard, description: "تفاصيل الرحلة ومعلومات العرض السياحي" },
  { id: 3, title: "الفنادق والإقامة", icon: Hotel, description: "تفاصيل الحجوزات الفندقية" },
  { id: 4, title: "المواصلات", icon: Car, description: "وسائل النقل والمواصلات" },
  { id: 5, title: "الجولات السياحية", icon: MapPin, description: "الأنشطة والجولات المقترحة" },
  { id: 6, title: "شمول", icon: CheckCircle, description: "ما يشمله وما لا يشمله البرنامج" },
  { id: 7, title: "الخدمات المشمولة", icon: Package, description: "الخدمات الإضافية والمزايا" },
  { id: 8, title: "التسعير والعروض", icon: DollarSign, description: "تفاصيل الأسعار والخصومات" },
  { id: 9, title: "ملخص العرض", icon: FileText, description: "مراجعة نهائية وإنشاء PDF" }
];

const CreatePackage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  
  // Use draft system
  const {
    draftData,
    updateDraft,
    saveDraft,
    clearDraft,
    lastSaved,
    isSaving,
  } = usePackageDraft(initialPackageData);
  
  const [packageData, setPackageData] = useState<PackageData>(draftData);

  // Sync with draft when packageData changes
  useEffect(() => {
    updateDraft(packageData);
  }, [packageData]);

  const updatePackageData = (stepData: Partial<PackageData>) => {
    setPackageData(prev => {
      const newData = { ...prev, ...stepData };
      
      // إذا تم إلغاء تفعيل خطوة معينة، نحذف البيانات المرتبطة بها
      if (stepData.includesHotels === false) {
        newData.hotels = [];
      }
      if (stepData.includesTransport === false) {
        newData.transport = [];
      }
      if (stepData.includesTours === false) {
        newData.tours = [];
      }
      if (stepData.includesServices === false) {
        newData.services = [];
      }
      
      return newData;
    });
  };

  const markStepCompleted = (step: number) => {
    if (!completedSteps.includes(step)) {
      setCompletedSteps(prev => [...prev, step]);
    }
  };

  const nextStep = () => {
    // Validate current step before moving forward
    if (currentStep === 1) {
      const validation = validateClientInfo(packageData);
      if (!validation.success) {
        const firstError = Object.values(validation.errors)[0];
        toast.error(firstError || "يرجى إكمال البيانات المطلوبة");
        return;
      }
    } else if (currentStep === 2) {
      const validation = validateTripDetails(packageData);
      if (!validation.success) {
        const firstError = Object.values(validation.errors)[0];
        toast.error(firstError || "يرجى إكمال البيانات المطلوبة");
        return;
      }
    }

    // Mark step as completed and save draft
    markStepCompleted(currentStep);
    saveDraft();
    
    const nextAvailable = getNextAvailableStep();
    if (nextAvailable <= steps.length) {
      setCurrentStep(nextAvailable);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const prevStep = () => {
    const prevStepNumber = getPreviousAvailableStep();
    if (prevStepNumber >= 1) {
      setCurrentStep(prevStepNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getNextAvailableStep = (): number => {
    let nextStep = currentStep + 1;
    while (nextStep <= steps.length && !isStepRequired(nextStep)) {
      nextStep++;
    }
    return nextStep;
  };

  const getPreviousAvailableStep = (): number => {
    let prevStep = currentStep - 1;
    while (prevStep >= 1 && !isStepRequired(prevStep)) {
      prevStep--;
    }
    return prevStep;
  };

  const isStepRequired = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1: // معلومات العميل
      case 2: // معلومات العرض
      case 6: // شمول
      case 8: // التسعير
      case 9: // ملخص
        return true;
      case 3: // الفنادق
        return packageData.includesHotels;
      case 4: // المواصلات
        return packageData.includesTransport;
      case 5: // الجولات
        return packageData.includesTours;
      case 7: // الخدمات
        return packageData.includesServices;
      default:
        return true;
    }
  };

  const goToStep = (step: number) => {
    if (isStepRequired(step)) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFinish = async (templateId: string) => {
    try {
      await generatePackagePDF(packageData, templateId);
      clearDraft(); // Clear draft after successful creation
      console.log('Package created and saved successfully!', packageData);
      toast.success("تم إنشاء وحفظ العرض بنجاح!");
      setTimeout(() => {
        navigate('/tourism-packages');
      }, 1500);
    } catch (error) {
      console.error('Error creating package:', error);
      toast.error("حدث خطأ أثناء إنشاء العرض.");
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <ClientInfoStepSimple data={packageData} onUpdate={updatePackageData} onNext={nextStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 2:
        return <TripDetailsAndProgramStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 3:
        return <HotelStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 4:
        return <TransportStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 5:
        return <ToursStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 6:
        return <InclusionsStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 7:
        return <ServicesStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 8:
        return <PricingStep data={packageData} onUpdate={updatePackageData} onNext={nextStep} onPrev={prevStep} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      case 9:
        return <SummaryStep data={packageData} onPrev={prevStep} onFinish={handleFinish} completedSteps={completedSteps} onStepClick={goToStep} isStepRequired={isStepRequired} />;
      default:
        return null;
    }
  };

  const progress = (currentStep / steps.length) * 100;

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header with Auto-save */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">إنشاء عرض سياحي جديد</h1>
              <p className="text-gray-600">قم بإنشاء عرض سياحي كامل مع جميع التفاصيل</p>
            </div>
            <AutoSaveIndicator 
              lastSaved={lastSaved} 
              isSaving={isSaving}
              onManualSave={saveDraft}
            />
          </div>
          
          {/* Progress Indicator */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {steps.map((step, index) => {
              const stepNumber = index + 1;
              const isCompleted = completedSteps.includes(stepNumber);
              const isCurrent = currentStep === stepNumber;
              const isRequired = isStepRequired(stepNumber);
              
              return (
                <div key={stepNumber} className="flex items-center flex-shrink-0">
                  <div
                    onClick={() => goToStep(stepNumber)}
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center font-bold cursor-pointer transition-all
                      ${isCurrent ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : ''}
                      ${isCompleted ? 'bg-green-500 text-white' : ''}
                      ${!isCurrent && !isCompleted ? 'bg-muted text-muted-foreground' : ''}
                      ${!isRequired ? 'opacity-50' : ''}
                    `}
                    title={step.title}
                  >
                    {isCompleted ? '✓' : stepNumber}
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-8 h-1 ${isCompleted ? 'bg-green-500' : 'bg-muted'}`} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-muted-foreground mt-2 text-center">
              الخطوة {currentStep} من {steps.length}
            </p>
          </div>
        </div>

        {/* Main Content with Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2">
            {renderStepContent()}
          </div>
          
          {/* Sidebar Summary */}
          <div className="lg:col-span-1">
            <PackageSidebarSummary 
              data={packageData} 
              completedSteps={completedSteps}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePackage;
